import React, { useEffect, useState } from 'react';
import { Line } from 'react-chartjs-2';
import api from '../../../api/axiosInstance';
import { useModal } from '../../../contexts/ModalContext';

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

import annotationPlugin from 'chartjs-plugin-annotation';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  annotationPlugin
);

// Helper function to generate a gradient fill for a given color
const getGradient = (ctx, chartArea, color) => {
  const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
  gradient.addColorStop(0, color);
  gradient.addColorStop(1, 'rgba(255,255,255,0)'); // fade to transparent
  return gradient;
};

const MonthlyLineChart = () => {
  const [chartData, setChartData] = useState(null);
  const { openModal } = useModal();

  // Define colors as constants to ensure consistency
  const colors = {
    delivered: 'green',
    bounced: 'red',
    opened: 'blue',
    clicked: 'yellow',
    spam: 'orange'
  };

  useEffect(() => {
    const fetchMonthlyReports = async () => {
      try {
        const response = await api.get('/reports/monthly');
        const monthlyData = response.data.reports;
        console.log(monthlyData);
        
        // Prepare chart labels and compute percentage datasets.
        // If totalSent is 0 then percentage is set to 0.
        const labels = monthlyData.map((item) => item.month);
        const deliveredData = monthlyData.map((item) =>
          item.totalSent ? (item.delivered / item.totalSent) * 100 : 0
        );
        const bouncedData = monthlyData.map((item) =>
          item.totalSent ? (item.bounced / item.totalSent) * 100 : 0
        );
        const openData = monthlyData.map((item) =>
          item.totalSent ? (item.open / item.totalSent) * 100 : 0
        );
        const clickData = monthlyData.map((item) =>
          item.totalSent ? (item.click / item.totalSent) * 100 : 0
        );
        const complaintData = monthlyData.map((item) =>
          item.totalSent ? (item.complaint / item.totalSent) * 100 : 0
        );

        setChartData({
          labels,
          datasets: [
            {
              label: 'Delivered (%)',
              data: deliveredData,
              fill: true,
              borderColor: colors.delivered,
              backgroundColor: (context) => {
                const { ctx, chartArea } = context.chart;
                if (!chartArea) return null;
                return getGradient(ctx, chartArea, 'rgba(0,128,0,0.3)');
              },
              tension: 0.1,
              pointHoverRadius: 6,
              pointHoverBackgroundColor: 'darkgreen',
            },
            {
              label: 'Bounced (%)',
              data: bouncedData,
              fill: true,
              borderColor: colors.bounced,
              backgroundColor: (context) => {
                const { ctx, chartArea } = context.chart;
                if (!chartArea) return null;
                return getGradient(ctx, chartArea, 'rgba(255,0,0,0.3)');
              },
              tension: 0.1,
              pointHoverRadius: 6,
              pointHoverBackgroundColor: 'darkred',
            },
            {
              label: 'Opened (%)',
              data: openData,
              fill: true,
              borderColor: colors.opened,
              backgroundColor: (context) => {
                const { ctx, chartArea } = context.chart;
                if (!chartArea) return null;
                return getGradient(ctx, chartArea, 'rgba(0,0,255,0.3)');
              },
              tension: 0.1,
              pointHoverRadius: 6,
              pointHoverBackgroundColor: 'darkblue',
            },
            {
              label: 'Clicked (%)',
              data: clickData,
              fill: true,
              borderColor: colors.clicked,
              backgroundColor: (context) => {
                const { ctx, chartArea } = context.chart;
                if (!chartArea) return null;
                return getGradient(ctx, chartArea, 'rgba(255,165,0,0.3)');
              },
              tension: 0.1,
              pointHoverRadius: 6,
              pointHoverBackgroundColor: 'darkorange',
            },
            {
              label: 'Spam (%)',
              data: complaintData,
              fill: true,
              borderColor: colors.spam,
              backgroundColor: (context) => {
                const { ctx, chartArea } = context.chart;
                if (!chartArea) return null;
                return getGradient(ctx, chartArea, 'rgba(128,0,128,0.3)');
              },
              tension: 0.1,
              pointHoverRadius: 6,
              pointHoverBackgroundColor: 'indigo',
            },
          ],
        });
      } catch (error) {
        openModal('failure', error.message);
      }
    };

    fetchMonthlyReports();
  }, [openModal]);

  // Chart options with percentage formatting and dotted threshold annotations.
  const chartOptions = {
    maintainAspectRatio: false,
    plugins: {
      tooltip: {
        mode: 'index',
        intersect: false,
        callbacks: {
          label: (context) => {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            label += `${context.parsed.y.toFixed(2)}%`;
            return label;
          },
        },
      },
      legend: {
        labels: {
          boxWidth: 12,
          boxHeight: 12,
        },
      },
      annotation: {
        annotations: {
          bounceThreshold: {
            type: 'line',
            scaleID: 'y',
            value: 1, // Bounce threshold at 1%
            borderColor: colors.bounced, // Using the same red as the bounce line
            borderWidth: 2,
            borderDash: [5, 5],
            label: {
              enabled: true,
              content: 'Bounce Threshold',
              position: 'end',
              backgroundColor: colors.bounced,
              color: '#fff',
              xAdjust: 0,
              yAdjust: -20,
              font: {
                weight: 'bold',
              },
              cornerRadius: 4,
              padding: 6,
            },
          },
          spamThreshold: {
            type: 'line',
            scaleID: 'y',
            value: 3, // Spam threshold at 3%
            borderColor: colors.spam, // Using the same purple as the spam line
            borderWidth: 2,
            borderDash: [5, 5],
            label: {
              enabled: true,
              content: 'Spam Threshold',
              position: 'end',
              backgroundColor: colors.spam,
              color: '#fff',
              xAdjust: 0,
              yAdjust: -20,
              font: {
                weight: 'bold',
              },
              cornerRadius: 4,
              padding: 6,
            },
          },
        },
      },
    },
    hover: {
      mode: 'nearest',
      intersect: true,
      onHover: (event, activeElements) => {
        event.native.target.style.cursor = activeElements.length > 0 ? 'pointer' : 'default';
      },
    },
    scales: {
      x: {
        grid: { display: false },
      },
      y: {
        grid: { display: false },
        ticks: {
          callback: (value) => `${value}%`,
        },
      },
    },
  };

  return (
    <div className="p-4 border rounded-lg shadow bg-white dark:bg-gray-800">
      {chartData ? (
        <div style={{ height: '300px' }}>
          <Line data={chartData} options={chartOptions} />
        </div>
      ) : (
        <div className="flex items-center justify-center w-full h-[300px]">
          <span>Loading chart...</span>
        </div>
      )}
    </div>
  );
};

export default MonthlyLineChart;